import { useMemo } from 'react';
import sortBy from 'lodash/sortBy';

import SubActivityView from '../sub-activity/sub-activity-view';
import { ViewProps } from './response.constants';
import * as Styled from './response-view.style';
import { useResponseBaseHeight } from './response.hooks';
import { useTeacherGetAnswers } from 'approot/activity-builder/present/activity-answers.hooks';
import { useActivityBuilderPresentContext } from 'approot/activity-builder/present/activity-builder-present.context';
import { useActivityBuilderFlags } from 'approot/activity-builder/activity-builder-flags';
import { useSimpleModalState } from 'approot/shared/modal/modal.hooks';
import ComponentOverlay from '../shared/component-overlay/component-overlay';
import { ComponentOverlayWrapper } from '../shared/component-overlay/component-overlay.style';
import { getComponentSelectorId } from '../shared/logic/get-component-selector-id';
import { getRichtextAnswer } from '../shared/logic/get-richtext-answer';
import ResponseFieldReadonly from './response-field-readonly';
import ResponseCheckAnswerTeacher from './response-check-answer-teacher';
import { getSubActivityInstructionAsText } from '../sub-activity/sub-activity.utils';
import { getActiveStudentAnswerCount } from './response.utils';

const ResponseView = (
  props: ViewProps & {
    isPublished: boolean;
  }
) => {
  const { id, properties, subActivityProperties, isPublished } = props;

  const baseHeight = useResponseBaseHeight();
  const { activityBuilderRespondFlag } = useActivityBuilderFlags();
  const { isVisible, openModal, closeModal } = useSimpleModalState();

  const sortedFields = useMemo(
    () => sortBy(properties.textResponse.fields, 'sort'),
    [properties.textResponse.fields]
  );

  const { componentAnswers, hasFetchedAnswers, error } = useTeacherGetAnswers({
    componentId: id,
    skip: !activityBuilderRespondFlag,
  });

  return subActivityProperties?.activityComponentId ? (
    <SubActivityView subActivityDetails={subActivityProperties}>
      {isVisible && (
        <ResponseCheckAnswerTeacher
          {...props}
          isPublished={isPublished}
          onClose={closeModal}
        />
      )}
      <ComponentOverlayWrapper>
        <Styled.ResponseViewContainer id={getComponentSelectorId(id)}>
          {sortedFields.map(field => {
            const answer = componentAnswers?.find(
              a => a.answerReferences?.id === field.itemId
            );
            return (
              <Styled.ResponseFieldWrapper key={field.itemId}>
                <ResponseFieldReadonly
                  answer={getRichtextAnswer(answer)}
                  baseHeight={baseHeight}
                  field={field}
                />
              </Styled.ResponseFieldWrapper>
            );
          })}
        </Styled.ResponseViewContainer>
        {isPublished && (
          <ComponentOverlay
            ariaLabel={`Type your answer to: ${getSubActivityInstructionAsText(
              subActivityProperties?.instructions
            )}`}
            componentId={id}
            hasFetchedAnswers={hasFetchedAnswers}
            error={error}
            onClick={activityBuilderRespondFlag ? openModal : undefined}
            getActiveStudentAnswerCount={answers =>
              getActiveStudentAnswerCount(answers)
            }
          />
        )}
      </ComponentOverlayWrapper>
    </SubActivityView>
  ) : null;
};

const ResponseViewWrapper = (props: ViewProps) => {
  const { isPublished } = useActivityBuilderPresentContext();

  return <ResponseView {...props} isPublished={isPublished} />;
};

export default ResponseViewWrapper;
